import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { AccountGroup, AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import {
  AssociationCardComponent,
  BrandingService,
  CardAction,
  CardData,
  COMPANY_PROFILE_CARD_DATA_TOKEN,
  CrmPlatformApiService,
  PageAnalyticsInjectionToken,
  pageAnalyticsInjectionTokenGenerator,
} from '@galaxy/crm/static';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, firstValueFrom, Observable, of, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { BusinessCenterService } from '../../../../core/business-center.service';
import { CompanyAccountService } from '../../../company-account.service';
import { AccessBusinessAppService } from '../../../profile-cards/association-cards/access-business-app/access-business-app.component';
import { AssociationPanelModule } from '../association-panel.module';
import { AppConfigService } from '../../../../../app/app-config.service';
import { HttpErrorResponse } from '@angular/common/http';

const ACCOUNT_GROUP_ROUTE = (accountGroupId: string) => `/businesses/accounts/${accountGroupId}/details`;

@Component({
  selector: 'app-associated-account',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatExpansionModule,
    MatCardModule,
    MatIconModule,
    GalaxyBadgeModule,
    MatMenuModule,
    GalaxyLoadingSpinnerModule,
    TranslateModule,
    AssociationPanelModule,
    AssociationCardComponent,
  ],
  providers: [
    {
      provide: PageAnalyticsInjectionToken,
      useFactory: pageAnalyticsInjectionTokenGenerator('Company'),
    },
    AccessBusinessAppService,
    BrandingService,
    CrmPlatformApiService,
    AccountGroupService,
    TranslateService,
  ],
  templateUrl: './associated-account.component.html',
  styleUrls: ['./associated-account.component.scss'],
})
export class AssociatedAccountComponent {
  data = inject(COMPANY_PROFILE_CARD_DATA_TOKEN);

  private companyAccountService = inject(CompanyAccountService);
  private accountGroupService = inject(AccountGroupService);
  private businessCenterService = inject(BusinessCenterService);
  private translateService = inject(TranslateService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly appConfigService = inject(AppConfigService);

  private readonly showPanel$$ = new BehaviorSubject<boolean>(false);
  protected showPanel$ = this.showPanel$$.asObservable();
  isAccountGroupLoading = toSignal(this.companyAccountService.createAccountGroupLoading$(this.data.company_id));
  accountGroupId$: Observable<string> = this.companyAccountService.accountGroupId$;
  private accountGroup$: Observable<AccountGroup | null> = this.accountGroupId$.pipe(
    distinctUntilChanged(),
    switchMap((accountGroupId) => {
      if (!accountGroupId) {
        return of(null);
      }

      return this.accountGroupService.get(
        accountGroupId,
        new ProjectionFilter({
          napData: true,
          accountGroupExternalIdentifiers: true,
        }),
      );
    }),
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 403) {
        this.showPanel$$.next(true);
      }
      return throwError(() => error);
    }),
    shareReplay(1),
  );
  accountGroupInfo$: Observable<CardData> = this.accountGroup$.pipe(
    filter((accountGroup) => accountGroup !== null),
    map((accountGroup) => {
      const accountGroupId = accountGroup?.accountGroupId ?? '';
      return {
        type: 'account',
        accountGroupCompanyId: accountGroupId,
        accountGroupCompanyName: accountGroup?.napData?.companyName ?? '',
        profileButtonUrl: this.appConfigService.config?.userCanAccessAccounts
          ? ACCOUNT_GROUP_ROUTE(accountGroupId)
          : '',
        hideAvatar: true,
      };
    }),
  );
  accountGroupActions$: Observable<CardAction[]> = this.accountGroup$.pipe(
    filter((accountGroup) => accountGroup !== null),
    switchMap((accountGroup) => {
      const accountGroupId = accountGroup?.accountGroupId ?? '';
      return this.businessCenterService
        .getVBCEntryUrl(accountGroupId)
        .pipe(map((vbcEntryInfo) => ({ accountGroup, vbcEntryInfo })));
    }),
    map(({ accountGroup, vbcEntryInfo }) => {
      const accountGroupId = accountGroup?.accountGroupId ?? '';
      return [
        {
          label: this.translateService.instant('ASSOCIATIONS.ACCOUNT.OPEN_BUSINESS_APP') + ' ' + vbcEntryInfo.name,
          callback: () => {
            this.businessCenterService
              .getVBCEntryUrl(accountGroupId)
              .pipe(take(1))
              .subscribe((vbcEntryInfo) => {
                window.open(vbcEntryInfo.url, '_blank');
              });
          },
        },
      ];
    }),
  );

  constructor() {
    this.companyAccountService.setAccountGroupId(this.data.account_group_id ?? '');
    this.accountGroup$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.showPanel$$.next(true);
    });
  }

  createAccount() {
    if (this.data.account_group_id) {
      this.companyAccountService.setAccountGroupId(this.data.account_group_id);
      return;
    }
    const companyId = this.data.company_id;
    firstValueFrom(this.companyAccountService.fetchAccountGroupId$(companyId)).then((accountGroupId) => {
      if (accountGroupId) {
        this.companyAccountService.setAccountGroupId(accountGroupId);
      } else {
        this.snackbarService.openErrorSnack('CRM.ERROR.ACCOUNT_GROUP_CREATION_FAILED');
      }
    });
  }
}
